import { SelectOption } from "@shopify/polaris"
import i18n from "i18next"
import { chunk, cloneDeep, isArray, keys, set } from "lodash-es"
import { action, computed, makeObservable, observable } from "mobx"

import { ITrackingPage } from "@/models/ITrackingPage"
import {
  AdditionalTextSetting,
  AutoTranslate,
  BrandingConfig,
  Child,
  CustomCSSAndHTML,
  CustomOrderInput,
  CustomOrderStatus,
  DateAndTimeFormat,
  DisplayOption,
  ITrackingPageOptions,
  PageTranslations,
  PostEddProviders,
  PostPurchaseEDD,
  ProRecommendAndUpsell,
  SEOOptimization,
  TrackingPageUrl,
  TranslateTrackingDetailedInfo,
  Translations,
  TranslationsConfig,
} from "@/models/ITrackingPageOptions"
import { defaultEDDConfig } from "@/stores/tracking_page/defaultConfig"

export default class TrackingPageStore implements ITrackingPage, ITrackingPageOptions {
  @observable public loadedDataTime: number = 0

  @observable public displayOption: DisplayOption | undefined
  @observable public pageTranslations: PageTranslations | undefined
  @observable public autoTranslate: AutoTranslate | undefined
  @observable public pageTranslationsD: PageTranslations | undefined
  @observable public customOrderStatus: CustomOrderStatus | undefined
  @observable public customOrderInput: CustomOrderInput | undefined
  @observable public post_purchase_edd: PostPurchaseEDD = defaultEDDConfig // 新版EDD
  @observable public post_edd_providers: PostEddProviders = [] // 新版EDD
  @observable public proRecommendAndUpsell: ProRecommendAndUpsell | undefined
  @observable public additionalTextSetting: AdditionalTextSetting | undefined
  @observable public seoOptimization: SEOOptimization | undefined
  @observable public dateAndTimeFormat: DateAndTimeFormat | undefined
  @observable public translateTrackingDetailedInfo: TranslateTrackingDetailedInfo[] | undefined
  @observable public customCssAndHtml: CustomCSSAndHTML | undefined
  @observable public brandingConfig: BrandingConfig | undefined
  @observable public productCollection: any | undefined
  @observable public trackingPageUrl: TrackingPageUrl | undefined

  @observable public translations: Translations | undefined
  @observable public translations_config: TranslationsConfig | undefined

  // 推荐商品选择, 显示未报错
  @observable public recommendationSelectCollectionError: boolean = false

  // Return 安装状态
  @observable public returnInstalled: boolean = false

  // Trust Reviews 安装状态
  @observable public trustInstalled: boolean = false

  constructor() {
    makeObservable(this)
  }

  @computed
  get options(): ITrackingPageOptions {
    const data: ITrackingPageOptions = {
      additionalTextSetting: undefined,
      autoTranslate: undefined,
      brandingConfig: undefined,
      customCssAndHtml: undefined,
      customOrderInput: undefined,
      customOrderStatus: undefined,
      dateAndTimeFormat: undefined,
      displayOption: undefined,
      pageTranslations: undefined,
      pageTranslationsD: undefined,
      proRecommendAndUpsell: undefined,
      productCollection: undefined,
      seoOptimization: undefined,
      translateTrackingDetailedInfo: undefined,
      trackingPageUrl: undefined,
      translations_config: undefined,
      translations: undefined,
      post_purchase_edd: undefined,
      post_edd_providers: undefined,
    }

    let currentKeys: (keyof ITrackingPageOptions)
    // @ts-ignore
    // .... 😶这里直接变量this，会导致后面加变量都得排除一下，不然就触发保存条。

    for (currentKeys in this) {
      // @ts-ignore
      const excludeKeys = [
        "loadedDataTime",
        "recommendationSelectCollectionError",
        "returnInstalled",
        "trustInstalled",
      ]

      if (excludeKeys.includes(currentKeys)) continue
      // @ts-ignore
      data[currentKeys] = cloneDeep(this[currentKeys])
    }

    return data
  }

  @computed
  get pageTranslationsChunk(): string[][] {
    return chunk(keys(this.pageTranslations), 2)
  }

  @computed
  get trackPageUrl(): string {
    const { subpathPrefix, subpath } = this.trackingPageUrl || {}

    return `/${subpathPrefix || "apps"}/${subpath || "parcelpanel"}`
  }

  @computed
  get publishedLanguagesOptions(): SelectOption[] | undefined {
    return this.translations_config?.locales.map(({ value, name }) => {
      const label = this.translations_config?.primary_locale === value
        ? `${name} (${i18n.t("tracking_page:PublishedLanguages.defaultText")})`
        : name

      return {
        value,
        label,
      } as SelectOption
    })
  }

  @action.bound
  public setProductCollection(data: any): any {
    this.productCollection = data
  }

  @action.bound
  public setBaseOptions(data: Partial<ITrackingPageOptions>): boolean {
    let currentKeys: (keyof ITrackingPageOptions)

    for (currentKeys in data) {
      if (currentKeys === "translateTrackingDetailedInfo" && (data[currentKeys]?.length || 0) === 0) {
        data[currentKeys] = [{ after: "", before: "" }]
      }

      // 换行符号导致 进行处理
      if (currentKeys === "customCssAndHtml" && data[currentKeys]) {
        // @ts-ignore
        const { htmlBottomCode } = data[currentKeys]
        // @ts-ignore

        data[currentKeys].htmlBottomCode = htmlBottomCode.replace(/\r\n/g, "\n")
      }

      // @ts-ignore
      this[currentKeys] = data[currentKeys]
    }
    return true
  }

  @action.bound
  public setOption<T>(type: keyof ITrackingPageOptions, key: keyof T, val: any): boolean {
    // @ts-ignore
    this[type][key] = val
    return true
  }

  @action.bound
  public setOptions<T>(type: keyof ITrackingPageOptions, val: T): boolean {
    if (isArray(val)) {
      // @ts-ignore
      this[type] = val
    } else {
      // @ts-ignore
      this[type] = { ...this[type], ...val }
    }

    return true
  }

  @action.bound
  public setEDDTargetConfig<T>(path: string, config: T): void {
    set(this.post_purchase_edd, path, config)
  }

  @action.bound
  public setCustomOrderStatus(customOrderStatusIndex: keyof CustomOrderStatus, value: Child[]): boolean {
    this.customOrderStatus![customOrderStatusIndex].children = value
    return true
  }

  @action.bound
  public filterProductRecommendationsOptions() {
    const options = this.proRecommendAndUpsell?.productRecommendations

    if (!options) {
      return
    }

    // if (!options.adv_coll_enabled || !options.collect) {
    //   /* 复选框 与 下拉框 必须同时选中，否则还原为未选择 */
    //   options.adv_coll_enabled = false
    //   options.collect = 0
    // }

    return cloneDeep(options)
  }

  @action.bound
  public addAnotherHandle(item: TranslateTrackingDetailedInfo): boolean {

    const asbodLists = cloneDeep(this.translateTrackingDetailedInfo) || []

    asbodLists.push(item)
    this.translateTrackingDetailedInfo = asbodLists
    return true
  }

  @action.bound
  public removeAnotherHandle(): any {

    const a = this.translateTrackingDetailedInfo?.filter(o => o.before !== "") || []

    if (a.length < 1) {
      a.push({
        after: "",
        before: "",
      })
    }
    this.translateTrackingDetailedInfo = a
    return a
  }

  @action.bound
  public setLoadedDataTime(time: number): void {
    this.loadedDataTime = time
  }
  @action.bound
  public setRecommendationSelectCollectionError(val: boolean): void {
    this.recommendationSelectCollectionError = val
  }

  /**
   * 清理 non_business_day_periods 空项
   */
  @action.bound
  public clearNonBusinessDayPeriods(): PostPurchaseEDD["non_business_day_periods"] {
    const newNonBusinessDayPeriods = this.post_purchase_edd?.non_business_day_periods
      ?.filter(v => v) || []

    this.setEDDTargetConfig("non_business_day_periods", newNonBusinessDayPeriods)

    return newNonBusinessDayPeriods
  }

  /**
   * 更新 EDD Provider 的状态
   */
  @action.bound
  public updateEDDProvider(providerId: string, updates: Partial<{ enabled: boolean; sortable: boolean }>): void {
    const providerIndex = this.post_edd_providers.findIndex(provider => provider.id === providerId)

    if (providerIndex !== -1) {
      // 创建新数组，确保 MobX 能检测到变化
      this.post_edd_providers = this.post_edd_providers.map((provider, index) =>
        index === providerIndex
          ? { ...provider, ...updates }
          : provider,
      )
    }
  }

  /**
   * 更新 EDD Providers 的排序
   */
  @action.bound
  public updateEDDProvidersOrder(newOrder: string[]): void {
    // 创建一个新的排序数组
    const orderedProviders = newOrder
      .map(id => this.post_edd_providers.find(provider => provider.id === id))
      .filter(Boolean) as PostEddProviders

    // 添加任何不在新排序中的provider（防止数据丢失）
    const existingIds = new Set(newOrder)
    const remainingProviders = this.post_edd_providers.filter(provider => !existingIds.has(provider.id))

    this.post_edd_providers = [...orderedProviders, ...remainingProviders]
  }

  /**
   * 设置 EDD Providers 数据
   */
  @action.bound
  public setEDDProviders(providers: PostEddProviders): void {
    this.post_edd_providers = providers
  }
  /**
   * 设置 Return安装状态
   */
  @action.bound
  public setReturnInstalled(status: boolean): void {
    this.returnInstalled = status
  }
  /**
   * 设置 Return安装状态
   */
  @action.bound
  public setTrustInstalled(status: boolean): void {
    this.trustInstalled = status
  }
}
